import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { valueToString } from "helpers/Helpers";

import Card from "components/card/Card";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";

import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague, ILeagueParticipant } from "redux/reducers/models/league";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { ReverseCreditBookDistribution } from "api/rpc/2024-04/facilityAdmin/client/creditBook";
import "./prizeAccounts.scss";

import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { ButtonNew as Button } from "components/buttonNew";
import { ISplitButtonProps } from "components/splitButton/SplitButton";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import PrizeAccountAdjustmentModal from "./PrizeAccountAdjustmentModal";

import {
  DistributeCreditBook,
  GetCreditBook,
  GetCreditBookTransactions,
  PutCreditBook,
  PostAdjustment,
} from "api/rpc/2024-04/facilityAdmin/client/admin/creditBook";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetParticipant } from "api/rpc/2024-04/facilityAdmin/league/participant";
import { GetTournamentParticipant } from "api/rpc/2024-04/facilityAdmin/tournament/participant";
import DataTable from "../../../../../components/dataTable/DataTable";
import { ICreditBookTransaction } from "redux/reducers/models/creditBook";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import Avatar from "components/avatar/Avatar";

interface IFilterState {
  tournamentSearch: string;
  leagueSearch: string;
  distributeToPlayersVisible: boolean;
  tournaments: Array<ITournament>;
  selectedTournament: ITournament;
  tournamentSearching: boolean;
  leagues: Array<ILeague>;
  leagueSearching: boolean;
  selectedLeague: ILeague;
  confirmDistributeFundsVisible: boolean;
  prizeAccountEvent: ILeague | ITournament;
  prizeAccountTransactions: ICreditBookTransaction[];
  prizeAccountTransactionsTableOffset: number;
}

interface IPrizeAccount {
  id: number;
  balance?: number;
  client_id?: number;
  currency?: string;
  customer_id?: number;
  facility_id?: number;
  league_id?: number;
  tournament_id?: number;
  type?: "prize_account" | "credit_book";
  title: string;
  closed?: boolean;
}

interface IPlayerState {
  allParticipantsInEvent: any[];
  selectedPlayersToDistribute: any[];
  masterParticipantList: any[];
  playerSearch: string;
}

const PRIZE_ACCOUNT_TRANSACTIONS_TABLE_LIMIT = 50;

export default function PrizeAccount() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { authStore } = useAppSelector(store => store);
  const { id, leagueId, tournamentId }: any = useParams();
  const { displayCurrency } = useCurrencyFacility();
  const permissions = authStore.user?.permissions;
  const [prizeAccount, setPrizeAccount] = useState<IPrizeAccount>(undefined);
  const [viewAdjustPrizeAccounts, setViewAdjustPrizeAccounts] = useState<boolean>(false);

  const [filterState, setFilterState] = useState<IFilterState>({
    tournamentSearch: "",
    leagueSearch: "",
    distributeToPlayersVisible: false,
    tournaments: [],
    selectedTournament: null,
    tournamentSearching: false,
    leagues: [],
    leagueSearching: false,
    selectedLeague: null,
    confirmDistributeFundsVisible: false,
    prizeAccountEvent: null,
    prizeAccountTransactions: [],
    prizeAccountTransactionsTableOffset: 0,
  });

  const [playerState, setPlayerState] = useState<IPlayerState>({
    allParticipantsInEvent: [],
    selectedPlayersToDistribute: [],
    masterParticipantList: [],
    playerSearch: "",
  });

  const { state: reverseModal, closeModal: closeReverseModal, updateModal: updateReverseModal } = useModal();

  const [deletePrizeAccount, setDeletePrizeAccount] = useState({
    openModal: false,
  });

  useEffect(() => {
    void getPrizeAccount();
    void loadPrizeAccountTransactions(filterState.prizeAccountTransactionsTableOffset);
  }, []);

  async function getPrizeAccount() {
    const getPrizeAccountsRes = await GetCreditBook({ id: id }, true);

    let prizeAccount: any;

    if (getPrizeAccountsRes.status === StatusCode.OK) {
      prizeAccount = getPrizeAccountsRes.data[0];
    } else {
      dispatch(showError(getPrizeAccountsRes.message));
      return;
    }

    if (prizeAccount.tournament_id) {
      //load tournament & players
      void loadTournamentParticipants(prizeAccount.tournament_id);
      void loadTournaments(prizeAccount.tournament_id);
    }

    if (prizeAccount.league_id) {
      //load league & players
      void loadLeagueParticipants(prizeAccount.league_id);
      void loadLeagues(prizeAccount.league_id);
    }

    setPrizeAccount({
      id: prizeAccount?.id,
      title: prizeAccount?.title,
      balance: prizeAccount?.balance,
      currency: prizeAccount?.currency,
      customer_id: prizeAccount?.customer_id,
      facility_id: prizeAccount?.facility_id,
      league_id: prizeAccount?.league_id,
      tournament_id: prizeAccount?.tournament_id,
      client_id: prizeAccount?.client_id,
      type: prizeAccount?.type,
      closed: prizeAccount?.closed,
    });

    setFilterState(prevState => ({
      ...prevState,
      title: prizeAccount.title,
    }));
  }

  function handleOpenDistributeFunds() {
    setFilterState(prevState => ({ ...prevState, distributeToPlayersVisible: true }));
  }

  function handleNavigateToEditPage(path: string) {
    history.push(path);
  }

  function handleCloseDistribute() {
    setFilterState(prevState => ({ ...prevState, distributeToPlayersVisible: false }));
    setPlayerState(prevState => ({
      ...prevState,
      allParticipantsInEvent: playerState.masterParticipantList,
      selectedPlayersToDistribute: [],
      playerSearch: "",
    }));
  }

  async function loadLeagueParticipants(league_id: number) {
    const params = {
      league_id: league_id,
    };

    const leaguePlayersRes = await GetParticipant(params, true);

    const players: any = leaguePlayersRes.data;

    for (let i = 0; i < players.length; i++) {
      players[i].distribute_value = "";
    }

    //Sort player list
    leaguePlayersRes.data.sort((a, b) => {
      const textA = a.customer.full_name.toUpperCase();
      const textB = b.customer.full_name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });

    if (leaguePlayersRes.status === StatusCode.OK) {
      setPlayerState(prevState => ({
        ...prevState,
        allParticipantsInEvent: leaguePlayersRes.data,
        masterParticipantList: leaguePlayersRes.data,
      }));
    }
  }

  async function loadTournamentParticipants(tournament_id: number) {
    const params = {
      tournament_id: tournament_id,
    };

    const tournamentPlayersRes = await GetTournamentParticipant(params, true);
    const players: any = tournamentPlayersRes.data;

    for (let i = 0; i < players.length; i++) {
      players[i].distribute_value = "";
    }

    //Sort player list
    tournamentPlayersRes.data.sort((a, b) => {
      const textA = a.customer.full_name.toUpperCase();
      const textB = b.customer.full_name.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    });

    if (tournamentPlayersRes.status === StatusCode.OK) {
      setPlayerState(prevState => ({
        ...prevState,
        allParticipantsInEvent: tournamentPlayersRes.data,
        masterParticipantList: tournamentPlayersRes.data,
      }));
    }
  }

  function handleSelectParticipantToDistribute(selectedParticipant: any) {
    const updatedParticipantList = playerState.allParticipantsInEvent.filter(
      player => player?.user_id !== selectedParticipant.user_id,
    );

    const distributeParticipantList = playerState.selectedPlayersToDistribute;

    distributeParticipantList.push(selectedParticipant);

    setPlayerState(prevState => ({
      ...prevState,
      allParticipantsInEvent: updatedParticipantList,
      selectedPlayersToDistribute: distributeParticipantList,
    }));
  }

  function handleSelectParticipantToRemove(selectedParticipant: any) {
    selectedParticipant.distribute_value = "";

    const distributeParticipantList = playerState.selectedPlayersToDistribute.filter(
      player => player?.user_id !== selectedParticipant.user_id,
    );

    const updatedParticipantList = playerState.allParticipantsInEvent;

    updatedParticipantList.push(selectedParticipant);

    setPlayerState(prevState => ({
      ...prevState,
      allParticipantsInEvent: updatedParticipantList,
      selectedPlayersToDistribute: distributeParticipantList,
    }));
  }

  function handleConfirmDistributeFunds() {
    for (let i = 0; i < playerState.selectedPlayersToDistribute.length; i++) {
      if (playerState.selectedPlayersToDistribute[i].distribute_value === "") {
        // const errorMessage =
        //   String(playerState.selectedPlayersToDistribute[i]?.customer?.full_name) + " is missing a distribute value";
        dispatch(showError(t("All players must have a distribute value")));
        return;
      }
    }

    setFilterState(prevState => ({ ...prevState, confirmDistributeFundsVisible: true }));
  }

  async function handleDeletePrizeAccount() {
    const params = {
      id: prizeAccount.id,
      closed: !prizeAccount.closed,
    };
    const getDeleteRes = await PutCreditBook(params, true);

    if (getDeleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred closed prize account")));
    }

    dispatch(showSuccess(t("Prize Account has been successfully closed")));
    setDeletePrizeAccount(prev => ({ ...prev, openModal: !prev.openModal }));
    void handleNavigation();
  }

  async function handleDistributeFunds() {
    const players = playerState.selectedPlayersToDistribute;
    const distributionInput: any = [];

    for (let i = 0; i < players.length; i++) {
      const playerInformation = {
        customer_id: Number(players[i].user_id),
        amount: Number(players[i].distribute_value),
      };

      distributionInput.push(playerInformation);
    }

    const params = {
      distribution_input: distributionInput,
      credit_book_id: id,
    };

    const distributeRes = await DistributeCreditBook(params, true);

    if (distributeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error distributing funds")));
    }

    setPlayerState(prevState => ({
      ...prevState,
      allParticipantsInEvent: playerState.masterParticipantList,
      selectedPlayersToDistribute: [],
    }));
    setFilterState(prevState => ({
      ...prevState,
      distributeToPlayersVisible: false,
      confirmDistributeFundsVisible: false,
    }));

    void loadPrizeAccountTransactions(filterState.prizeAccountTransactionsTableOffset);
    void getPrizeAccount();
  }

  async function handleReverseDistributeFunds() {
    const reverseRes = await ReverseCreditBookDistribution({ credit_book_id: prizeAccount?.id }, true);

    if (reverseRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error reversing credit book distribution. Distribution may have already been reversed")));
      return;
    }

    closeReverseModal();
    dispatch(showSuccess(t("Distribution reversed successfully")));
    void loadPrizeAccountTransactions(filterState.prizeAccountTransactionsTableOffset);
    void getPrizeAccount();
  }

  function handleParticipantDistributeValueChange(event: any, participant: any) {
    const value = event.target.value;

    setPlayerState(prevState => ({
      ...prevState,
      selectedPlayersToDistribute: prevState.selectedPlayersToDistribute.map((player: ILeagueParticipant) =>
        player.user_id === participant.user_id ? { ...player, distribute_value: value } : player,
      ),
    }));
  }

  async function loadTournaments(tournament_id: number) {
    const tournamentRes = await GetTournament({ id: tournament_id }, true);

    if (tournamentRes.status === StatusCode.OK) {
      const tournaments = tournamentRes.data;
      setFilterState(prevState => ({ ...prevState, prizeAccountEvent: tournaments[0] }));
    } else {
      dispatch(showError(tournamentRes.message));
    }
  }

  async function loadLeagues(league_id: number) {
    const leagueRes = await GetLeague({ id: league_id }, true);

    if (leagueRes.status === StatusCode.OK) {
      const leagues = leagueRes.data;
      setFilterState(prevState => ({ ...prevState, prizeAccountEvent: leagues[0] }));
    } else {
      dispatch(showError(leagueRes.message));
    }
  }

  async function loadPrizeAccountTransactions(offset: number) {
    const transactionRes = await GetCreditBookTransactions(
      {
        credit_book_id: id,
        limit: PRIZE_ACCOUNT_TRANSACTIONS_TABLE_LIMIT,
        offset,
      },
      true,
    );

    if (transactionRes.status === StatusCode.OK) {
      setFilterState(prevState => ({ ...prevState, prizeAccountTransactions: transactionRes.data }));
    } else {
      dispatch(showError(transactionRes.message));
    }
  }

  async function handlePrizeAdjustment(amount: string) {
    const params = {
      amount: amount,
      credit_book_id: prizeAccount?.id,
    };

    const adjustRes = await PostAdjustment(params, true);
    if (adjustRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adjusting prize account balance")));
    }

    void loadPrizeAccountTransactions(filterState.prizeAccountTransactionsTableOffset);
    void getPrizeAccount();
    setViewAdjustPrizeAccounts(false);
  }

  function handleOpenAdjustment() {
    setViewAdjustPrizeAccounts(true);
  }

  function handlePrizeAccountTransactionsTableOffset(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        const updatedOffset = filterState.prizeAccountTransactionsTableOffset - PRIZE_ACCOUNT_TRANSACTIONS_TABLE_LIMIT;
        setFilterState(prev => ({ ...prev, prizeAccountTransactionsTableOffset: updatedOffset }));
        void loadPrizeAccountTransactions(updatedOffset);
        break;
      }
      case "next": {
        const updatedOffset = filterState.prizeAccountTransactionsTableOffset + PRIZE_ACCOUNT_TRANSACTIONS_TABLE_LIMIT;
        setFilterState(prev => ({ ...prev, prizeAccountTransactionsTableOffset: updatedOffset }));
        void loadPrizeAccountTransactions(updatedOffset);
        break;
      }
      default:
        return;
    }
  }

  function handleOpenDeletePrizeAccountModal() {
    setDeletePrizeAccount(prev => ({ ...prev, openModal: !prev.openModal }));
  }

  function handleNavigation() {
    leagueId
      ? history.push("/admin/league/" + String(leagueId) + "/prizeAccounts")
      : tournamentId
      ? history.push("/admin/tournament/" + String(tournamentId) + "/prizeAccounts")
      : history.push("/admin/settings/prizeAccounts");
  }

  const splitButtonAction: ISplitButtonProps = {
    label: t("Distribute Funds"),
    type: "primary",
    disabled: prizeAccount?.balance <= 0,
    onClick: () => handleOpenDistributeFunds(),
    options: [
      {
        label: t("Reverse Previous Distrubution"),
        onClick: () => updateReverseModal({ isOpen: true }),
        icon: "clock-rotate-left",
      },
    ],
  };

  const primaryAction = {
    content: t("Distribute Funds"),
    action: handleOpenDistributeFunds,
    disabled: prizeAccount?.closed,
  };

  return (
    <>
      <Page
        title={prizeAccount?.title}
        primaryAction={primaryAction}
        multipleActionDropdownAction={{
          label: t("More Options"),
          disabled: prizeAccount?.closed,
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Edit"),
                icon: "pencil",
                disabled: prizeAccount?.closed,
                handler: () =>
                  handleNavigateToEditPage(
                    leagueId
                      ? "/admin/league/" + String(leagueId) + "/prizeAccounts/edit/" + String(prizeAccount?.id)
                      : tournamentId
                      ? "/admin/tournament/" + String(tournamentId) + "/prizeAccounts/edit/" + String(prizeAccount?.id)
                      : "/admin/settings/prizeAccounts/edit/" + String(prizeAccount?.id),
                  ),
              },
              {
                type: "handler",
                disabled: !permissions.credit_books_make_adjustments,
                label: t("Adjustment"),
                icon: "arrow-up-arrow-down",
                handler: handleOpenAdjustment,
              },
              {
                type: "handler",
                disabled: !permissions.credit_books_make_adjustments,
                label: t("Close Prize Account"),
                icon: "circle-xmark",
                handler: handleOpenDeletePrizeAccountModal,
              },
              {
                type: "handler",
                disabled: prizeAccount?.closed,
                label: t("Reverse Previous Distribution"),
                handler: () => updateReverseModal({ isOpen: true }),
                icon: "clock-rotate-left",
              },
            ],
          },
        }}
        narrow
        breadcrumbs={[
          {
            prefix: true,
            label: t("Back to prize accounts"),
            url: leagueId
              ? "/admin/league/" + String(leagueId) + "/prizeAccounts"
              : tournamentId
              ? "/admin/tournament/" + String(tournamentId) + "/prizeAccounts"
              : "/admin/settings/prizeAccounts",
          },
        ]}
      >
        <>
          <Card>
            <Card.Section>
              <div>
                <p>
                  {" "}
                  {t("Balance:")}{" "}
                  {displayCurrency(prizeAccount?.balance, prizeAccount?.currency)}
                </p>

                <p>
                  {prizeAccount?.tournament_id
                    ? t("Tournament:")
                    : prizeAccount?.league_id
                    ? t("League:")
                    : ""}{" "}
                  {filterState?.prizeAccountEvent?.name}{" "}
                </p>
              </div>
            </Card.Section>

            <DataTable
              columns={[
                { label: t("Processed At") },
                { label: t("Kind") },
                { label: t("Currency") },
                { label: t("Amount") },
                { label: t("Order") },
              ]}
              footer={{
                tableLimit: PRIZE_ACCOUNT_TRANSACTIONS_TABLE_LIMIT,
                tableOffset: filterState.prizeAccountTransactionsTableOffset,
                handleTableOffset: handlePrizeAccountTransactionsTableOffset,
                disableNextOffset:
                  filterState.prizeAccountTransactions == null ||
                  filterState.prizeAccountTransactions.length < PRIZE_ACCOUNT_TRANSACTIONS_TABLE_LIMIT,
              }}
            >
              {filterState.prizeAccountTransactions.length > 0 ? (
                filterState.prizeAccountTransactions.map((transaction: ICreditBookTransaction, index: number) => {
                  return (
                    <tr key={index}>
                      <td style={{ width: "35%" }}>
                        <p>{transaction.processed_at_local}</p>
                      </td>
                      <td>
                        <p>{valueToString(transaction.kind)}</p>
                      </td>
                      <td>
                        <p>{String(transaction.currency).toUpperCase()}</p>
                      </td>

                      <td>
                        <p>{displayCurrency(transaction.amount, transaction?.currency)}</p>
                      </td>

                      <td>
                        {transaction.order_id ? (
                          <div
                            className="cursor-pointer customer-profile"
                            onClick={() => window.open(`/admin/order/${transaction.order_id}`)}
                          >
                            {`${t("Order")} ${
                              transaction.order_name
                            }`}
                            <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={faArrowUpRightFromSquare} size="1x" />
                          </div>
                        ) : null}
                        {transaction.invoice_id ? (
                          <div
                            className="cursor-pointer customer-profile"
                            onClick={() => window.open(`/admin/invoices/${transaction.invoice_id}`)}
                          >
                            {t("View Invoice")} {}
                            <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={faArrowUpRightFromSquare} size="1x" />
                          </div>
                        ) : null}
                        {transaction.customer_id && transaction.kind === "debit_transfer" ? (
                          <div
                            className="cursor-pointer customer-profile"
                            onClick={() =>
                              window.open("/admin/customers/" + String(transaction.customer_id) + "/credit")
                            }
                          >
                            {t("View Credit Book")} {}
                            <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={faArrowUpRightFromSquare} size="1x" />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td>{t("No Account Transactions")}</td>
                </tr>
              )}
            </DataTable>
          </Card>
        </>
      </Page>

      <Sheet
        title={t("Distribute To Players")}
        open={filterState.distributeToPlayersVisible}
        size="large"
        closable
        onCancel={handleCloseDistribute}
        onOk={handleConfirmDistributeFunds}
        okText={t("Distribute")}
      >
        <div className="distribute-players-container">
          <div className="distribute-players-container-left-side">
            <div className="distribute-players-container-title">
              {t("Player List")}
            </div>

            <div className="mt-2 mb-3">
              {/* <Search
          placeholder={t("Search customers by name, membership, address, or phone...")}
          historyKey={"search_customers_table"}
          searchCallback={(e) => setPlayerState((prevState) => ({ ...prevState, playerSearch: e.target.value }))}
        /> */}

              <Input
                placeholder={t("Search for player...")}
                id="player_search"
                value={playerState.playerSearch}
                onChange={e => setPlayerState(prevState => ({ ...prevState, playerSearch: e.target.value }))}
                labelHidden
              />
            </div>

            <div>
              {playerState.allParticipantsInEvent
                ?.filter(
                  player =>
                    String(player.customer.full_name).toLowerCase().includes(playerState.playerSearch.toLowerCase()) ||
                    String(player.customer.email).toLowerCase().includes(playerState.playerSearch.toLowerCase()) ||
                    String(player.customer.phone).toLowerCase().includes(playerState.playerSearch.toLowerCase()),
                )
                .map((participant: any, index: number) => {
                  return (
                    <div
                      className="distribute-players-container-player-name"
                      key={index}
                      onClick={() => handleSelectParticipantToDistribute(participant)}
                    >
                      <p className="flex gap-2 items-center">
                        <Avatar size="small" source={participant?.customer?.profile_image_source} />

                        {participant?.customer?.full_name}
                      </p>
                      <p style={{ fontSize: "12px" }}>
                        {participant?.customer?.email}
                        {participant?.customer?.phone && participant?.customer?.email ? " - " : ""}
                        {participant?.customer?.phone ? participant.customer.phone : ""}
                      </p>
                      <p style={{ fontSize: "12px" }}>
                        {participant?.customer?.customer_type ? participant?.customer?.customer_type : ""}
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="distribute-players-container-right-side">
            <div>
              <div className="distribute-players-container-title">
                {t("Distribute To")}
              </div>

              {playerState.selectedPlayersToDistribute?.map((participant: any, index: number) => {
                return (
                  <div className="flex justify-between items-center gap-1 mb-2" key={index}>
                    <div className="w-3/4">
                      <p className="flex gap-2 items-center">
                        <Avatar size="small" source={participant?.customer?.profile_image_source} />

                        {participant?.customer?.full_name}

                        <span style={{ fontSize: "12px" }}>
                          {participant?.customer?.customer_type ? " - " : ""}
                          {participant?.customer?.customer_type}
                        </span>
                      </p>

                      <p style={{ fontSize: "12px" }}>
                        {participant?.customer?.email}

                        {participant?.customer?.phone ? " - " : ""}
                        {participant?.customer?.phone}
                      </p>
                    </div>

                    <div style={{ width: "250px" }}>
                      <Input
                        prefix="$"
                        placeholder="0.00"
                        id="distribute_value"
                        value={participant?.distribute_value}
                        onChange={(event: any) => handleParticipantDistributeValueChange(event, participant)}
                        labelHidden
                      />
                    </div>

                    <div style={{ textAlign: "center", width: "30px" }}>
                      <button onClick={() => handleSelectParticipantToRemove(participant)}>
                        {" "}
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Sheet>

      <PrizeAccountAdjustmentModal
        open={viewAdjustPrizeAccounts}
        onOk={async amount => handlePrizeAdjustment(amount)}
        onClose={() => setViewAdjustPrizeAccounts(false)}
      />

      <Popup
        open={deletePrizeAccount.openModal}
        type="warning"
        title={t("Are you sure?")}
        description={t("Are you sure you want to close this prize account? This decision is permanent.")}
        onOk={handleDeletePrizeAccount}
        okText={t("Close Account")}
        onCancel={() => setDeletePrizeAccount(prev => ({ ...prev, openModal: !prev.openModal }))}
      />

      <Popup
        open={filterState.confirmDistributeFundsVisible}
        type="warning"
        title={t("Distribute Funds")}
        description={t("Are you sure you want to distribute funds?")}
        onOk={handleDistributeFunds}
        okText={t("Distribute")}
        onCancel={() => setFilterState(prevState => ({ ...prevState, confirmDistributeFundsVisible: false }))}
      />

      <Portal isMounted={reverseModal.isOpen}>
        <Popup
          open={reverseModal.isOpen}
          type="warning"
          title={t("Reverse Credit Book Distribution?")}
          description={t("Are you sure you want to reverse the last credit book distribution?")}
          onOk={handleReverseDistributeFunds}
          okText={t("Reverse")}
          onCancel={() => closeReverseModal()}
        />
      </Portal>
    </>
  );
}
